import { useMutation } from '@apollo/client';
import type { UpdateThirdPartyResponseStatusMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateThirdPartyResponseStatusDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { UpdateThirdPartyResponseStatusInput } from './useUpdateThirdPartyResponseStatusTRPC';
import { useUpdateThirdPartyResponseStatusTRPC } from './useUpdateThirdPartyResponseStatusTRPC';

/**
 * Flag-gated wrapper for the `updateThirdPartyResponseStatus` mutation
 * (RSP-6891). Defaults to the legacy Apollo mutation and delegates to the tRPC
 * variant when the `trpc` feature flag is enabled. Consumers import THIS, never
 * the `*TRPC` variant directly.
 */
export const useUpdateThirdPartyResponseStatus = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateStatusGraphQL, graphqlState] = useMutation(
    UpdateThirdPartyResponseStatusDocument
  );

  const trpcMutation = useUpdateThirdPartyResponseStatusTRPC();

  const updateStatus = async (
    input: UpdateThirdPartyResponseStatusInput
  ): Promise<UpdateThirdPartyResponseStatusMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateStatus(input);
    }

    const result = await updateStatusGraphQL({
      variables: {
        Action: input.Action,
        ResponseIds: input.ResponseIds,
        Reason: input.Reason,
        RequestType: input.RequestType,
        ShareWithRespondents: input.ShareWithRespondents,
        ThirdPartyId: input.ThirdPartyId,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update third party response status');
    }

    return result.data;
  };

  return {
    updateStatus,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
